"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var GenericGraphAdapter_1 = require("./GenericGraphAdapter");
var util_1 = require("./util");
function createMerger(edgeMerger, mapConstructor) {
    if (edgeMerger === void 0) { edgeMerger = util_1.takeFirst; }
    return function (first, second) {
        if (first === undefined) {
            return second || new mapConstructor();
        }
        if (second === undefined) {
            return first;
        }
        for (var it_1 = second.entries(), res = it_1.next(); !res.done; res = it_1.next()) {
            var data = first.get(res.value[0]);
            if (data === undefined) {
                data = res.value[1];
            }
            else {
                var other = res.value[1];
                data = other !== undefined ? edgeMerger(data, other) : data;
            }
            first.set(res.value[0], data);
        }
        return first;
    };
}
var MultiGraphAdapter = (function () {
    function MultiGraphAdapter(graphFactory, edgeCount, mapConstructor) {
        this.g = graphFactory();
        this.edgeCount = edgeCount;
        this.mapConstructor = mapConstructor;
    }
    MultiGraphAdapter.create = function (options) {
        if (options === void 0) { options = {}; }
        var graphFactory = options.graphFactory || GenericGraphAdapter_1.GenericGraphAdapter.create;
        var mapConstructor = options.mapConstructor || Map;
        return new MultiGraphAdapter(graphFactory, 0, mapConstructor);
    };
    MultiGraphAdapter.prototype.mapLabeled = function (vertexMapper, edgeDataMapper, labelMapper) {
        var _this = this;
        var g = this.g.map(vertexMapper, function (edgeLabelToEdgeDataMap) {
            var clonedEdgeLabelToEdgeDataMap = new _this.mapConstructor();
            edgeLabelToEdgeDataMap.forEach(function (edgeData, edgeLabel) {
                var clonedEdgeData = edgeData !== undefined ? edgeDataMapper(edgeData) : undefined;
                var clonedEdgeLabel = edgeLabel !== undefined ? labelMapper(edgeLabel) : undefined;
                clonedEdgeLabelToEdgeDataMap.set(clonedEdgeLabel, clonedEdgeData);
            });
            return clonedEdgeLabelToEdgeDataMap;
        });
        return new MultiGraphAdapter(function () { return g; }, this.edgeCount, this.mapConstructor);
    };
    MultiGraphAdapter.prototype.map = function (vertexMapper, edgeDataMapper) {
        return this.mapLabeled(vertexMapper, edgeDataMapper, function (label) { return label; });
    };
    MultiGraphAdapter.prototype.clone = function (vertexCloner, edgeDataCloner, labelCloner) {
        var vCloner = vertexCloner !== undefined ? vertexCloner : function (vertex) { return vertex; };
        var eCloner = edgeDataCloner !== undefined ? edgeDataCloner : function (data) { return data; };
        var lCloner = labelCloner !== undefined ? labelCloner : function (label) { return label; };
        return this.mapLabeled(vCloner, eCloner, lCloner);
    };
    MultiGraphAdapter.prototype.addLabeledEdge = function (from, to, label, data) {
        return this.addEdge(from, to, data, label);
    };
    MultiGraphAdapter.prototype.canAddEdge = function (from, to, label) {
        var srcData = this.g.getEdgeData(from, to);
        if (srcData !== undefined) {
            if (srcData.has(label)) {
                return false;
            }
            return true;
        }
        return this.g.canAddEdge(from, to);
    };
    MultiGraphAdapter.prototype.addEdge = function (from, to, data, label) {
        var srcData = this.g.getEdgeData(from, to);
        if (srcData !== undefined) {
            if (srcData.has(label)) {
                return false;
            }
            srcData.set(label, data);
            this.edgeCount += 1;
            return true;
        }
        this.edgeCount += 1;
        var newData = new Map();
        newData.set(label, data);
        return this.g.addEdge(from, to, newData);
    };
    MultiGraphAdapter.prototype.addVertex = function (vertex) {
        return this.g.addVertex(vertex);
    };
    MultiGraphAdapter.prototype.getEdgesWithDataTo = function (vertex, label) {
        return util_1.createFlatMappedIterator(this.g.getEdgesWithDataTo(vertex), function (pair) {
            var map = pair[1];
            if (map === undefined) {
                return util_1.EmptyIterator;
            }
            if (label === undefined) {
                return util_1.createMappedIterator(map.values(), function (edgeData) { return [pair[0], edgeData]; });
            }
            return util_1.createMappedIterator(util_1.createFilteredIterator(map.entries(), function (entry) { return entry[0] === label; }), function (entry) { return [pair[0], entry[1]]; });
        });
    };
    MultiGraphAdapter.prototype.getEdgesWithDataFrom = function (vertex, label) {
        return util_1.createFlatMappedIterator(this.g.getEdgesWithDataFrom(vertex), function (pair) {
            var map = pair[1];
            if (map === undefined) {
                return util_1.EmptyIterator;
            }
            if (label === undefined) {
                return util_1.createMappedIterator(map.values(), function (edgeData) { return [pair[0], edgeData]; });
            }
            return util_1.createMappedIterator(util_1.createFilteredIterator(map.entries(), function (entry) { return entry[0] === label; }), function (entry) { return [pair[0], entry[1]]; });
        });
    };
    MultiGraphAdapter.prototype.getEdgeDataTo = function (vertex, label) {
        if (label === undefined) {
            return util_1.createFilteredIterator(util_1.createFlatMappedIterator(this.g.getEdgeDataTo(vertex), function (data) { return data ? data.values() : util_1.EmptyIterator; }), function (data) { return data !== undefined; });
        }
        return util_1.createMappedIterator(util_1.createFilteredIterator(util_1.createFlatMappedIterator(this.g.getEdgeDataTo(vertex), function (data) { return data ? data.entries() : util_1.EmptyIterator; }), function (entry) { return entry[1] !== undefined && entry[0] === label; }), function (entry) { return entry[1]; });
    };
    MultiGraphAdapter.prototype.getEdgeDataFrom = function (vertex, label) {
        if (label === undefined) {
            return util_1.createFilteredIterator(util_1.createFlatMappedIterator(this.g.getEdgeDataFrom(vertex), function (data) { return data ? data.values() : util_1.EmptyIterator; }), function (data) { return data !== undefined; });
        }
        return util_1.createMappedIterator(util_1.createFilteredIterator(util_1.createFlatMappedIterator(this.g.getEdgeDataFrom(vertex), function (data) { return data.entries(); }), function (entry) { return entry[1] !== undefined && entry[0] === label; }), function (entry) { return entry[1]; });
    };
    MultiGraphAdapter.prototype.contractEdge = function (from, to, vertexMerger, edgeMerger) {
        var deletionCount = 0;
        var data = this.g.getEdgeData(from, to);
        if (data !== undefined) {
            deletionCount = data.size;
        }
        var wasContracted = this.g.contractEdge(from, to, vertexMerger, createMerger(edgeMerger, this.mapConstructor));
        if (wasContracted) {
            this.edgeCount -= deletionCount;
        }
        return wasContracted;
    };
    MultiGraphAdapter.prototype.contractLabeledEdge = function (from, to, label, vertexMerger, edgeMerger) {
        if (this.getEdgeCountBetween(from, to) !== 1) {
            return false;
        }
        if (!this.hasLabeledEdge(from, to, label)) {
            return false;
        }
        return this.contractEdge(from, to, vertexMerger, edgeMerger);
    };
    MultiGraphAdapter.prototype.canContractLabeledEdge = function (from, to, label) {
        if (this.getEdgeCountBetween(from, to) !== 1) {
            return false;
        }
        if (!this.hasLabeledEdge(from, to, label)) {
            return false;
        }
        return this.canContractEdge(from, to);
    };
    MultiGraphAdapter.prototype.canContractOneEdge = function (from, to) {
        if (this.getEdgeCountBetween(from, to) !== 1) {
            return false;
        }
        return this.canContractEdge(from, to);
    };
    MultiGraphAdapter.prototype.canContractEdge = function (from, to) {
        return this.g.canContractEdge(from, to);
    };
    MultiGraphAdapter.prototype.deleteLabeledEdge = function (from, to, label) {
        var srcData = this.g.getEdgeData(from, to);
        if (srcData === undefined) {
            return false;
        }
        var wasDeleted = srcData.delete(label);
        if (wasDeleted) {
            this.edgeCount -= 1;
        }
        if (srcData.size === 0) {
            this.g.deleteEdge(from, to);
        }
        return wasDeleted;
    };
    MultiGraphAdapter.prototype.deleteEdge = function (from, to, label) {
        if (label === undefined) {
            this.edgeCount -= this.getEdgeCountBetween(from, to);
            return this.g.deleteEdge(from, to);
        }
        return this.deleteLabeledEdge(from, to, label);
    };
    MultiGraphAdapter.prototype.deleteVertex = function (vertex) {
        var _this = this;
        util_1.forEach(function (data) {
            var edges = data[1];
            if (edges !== undefined) {
                _this.edgeCount -= edges.size;
            }
        }, this.g.getEdgesWithDataFrom(vertex), this.g.getEdgesWithDataTo(vertex));
        return this.g.deleteVertex(vertex);
    };
    MultiGraphAdapter.prototype.getLabeledEdgeCount = function () {
        return this.edgeCount;
    };
    MultiGraphAdapter.prototype.getEdgeCount = function () {
        return this.g.getEdgeCount();
    };
    MultiGraphAdapter.prototype.getEdgeData = function (from, to, label) {
        var data = this.g.getEdgeData(from, to);
        if (data === undefined) {
            return undefined;
        }
        return data.get(label);
    };
    MultiGraphAdapter.prototype.setEdgeData = function (from, to, data, label) {
        var d = this.g.getEdgeData(from, to);
        if (d === undefined || !d.has(label)) {
            return false;
        }
        d.set(label, data);
        return true;
    };
    MultiGraphAdapter.prototype.getEdges = function () {
        return this.g.getEdges();
    };
    MultiGraphAdapter.prototype.getEdgesWithData = function () {
        return util_1.createFlatMappedIterator(this.g.getEdgesWithData(), function (entry) {
            return util_1.createMappedIterator(entry[2].values(), function (data) { return [
                entry[0],
                entry[1],
                data
            ]; });
        });
    };
    MultiGraphAdapter.prototype.getLabeledEdgesWithData = function () {
        return util_1.createFlatMappedIterator(this.g.getEdgesWithData(), function (entry) {
            return util_1.createMappedIterator(entry[2].entries(), function (subEntry) { return [
                entry[0],
                entry[1],
                subEntry[1],
                subEntry[0]
            ]; });
        });
    };
    MultiGraphAdapter.prototype.getEdgeCountBetween = function (from, to) {
        var data = this.g.getEdgeData(from, to);
        if (data === undefined) {
            return 0;
        }
        return data.size;
    };
    MultiGraphAdapter.prototype.getEdgeLabels = function (from, to) {
        var data = this.g.getEdgeData(from, to);
        if (data === undefined) {
            return util_1.EmptyIterator;
        }
        return data.keys();
    };
    MultiGraphAdapter.prototype.getLabeledEdges = function () {
        var _this = this;
        return util_1.createFlatMappedIterator(this.g.getEdges(), function (edge) {
            var data = _this.g.getEdgeData(edge[0], edge[1]);
            if (data === undefined) {
                return util_1.EmptyIterator;
            }
            return util_1.createMappedIterator(data.keys(), function (key) { return [edge[0], edge[1], key]; });
        });
    };
    MultiGraphAdapter.prototype.getPredecessorsOf = function (vertex, label) {
        var _this = this;
        if (label === undefined) {
            return this.g.getPredecessorsOf(vertex);
        }
        return util_1.createFilteredIterator(this.g.getPredecessorsOf(vertex), function (from) {
            var data = _this.g.getEdgeData(from, vertex);
            return data !== undefined && data.has(label);
        });
    };
    MultiGraphAdapter.prototype.getSuccessorsOf = function (vertex, label) {
        var _this = this;
        if (label === undefined) {
            return this.g.getSuccessorsOf(vertex);
        }
        return util_1.createFilteredIterator(this.g.getSuccessorsOf(vertex), function (to) {
            var data = _this.g.getEdgeData(vertex, to);
            return data !== undefined && data.has(label);
        });
    };
    MultiGraphAdapter.prototype.getOrder = function (vertex) {
        return this.g.getOrder(vertex);
    };
    MultiGraphAdapter.prototype.supportsOrder = function () {
        return this.g.supportsOrder();
    };
    MultiGraphAdapter.prototype.getVertexCount = function () {
        return this.g.getVertexCount();
    };
    MultiGraphAdapter.prototype.getVertices = function () {
        return this.g.getVertices();
    };
    MultiGraphAdapter.prototype.hasEdge = function (from, to, label) {
        var data = this.g.getEdgeData(from, to);
        return data !== undefined && (label === undefined || data.has(label));
    };
    MultiGraphAdapter.prototype.hasLabeledEdge = function (from, to, label) {
        var data = this.g.getEdgeData(from, to);
        return data !== undefined && data.has(label);
    };
    MultiGraphAdapter.prototype.hasVertex = function (vertex) {
        return this.g.hasVertex(vertex);
    };
    MultiGraphAdapter.prototype.isReachable = function (source, target) {
        return this.g.isReachable(source, target);
    };
    return MultiGraphAdapter;
}());
exports.MultiGraphAdapter = MultiGraphAdapter;
